import React from "react";
import { ArrowUp, ArrowDown } from "lucide-react";

/**
 * Reusable Table Header component with sorting
 *
 * Supports two column formats:
 * - Legacy: { field, label, sortable, className }
 * - New:    { header, accessor, sortable, className }
 */
export default function TableHeader({
  columns,
  onSort,
  sortField,
  sortOrder,
  showCheckbox = false,
  onCheckAll = () => {},
  isAllChecked = false,
}) {
  return (
    <thead className="bg-gradient-to-r from-gray-50 to-gray-100/80 border-b border-gray-200">
      <tr>
        {showCheckbox && (
          <th scope="col" className="px-6 py-4">
            <div className="inline-flex items-center">
              <label className="flex items-center cursor-pointer relative group">
                <input
                  type="checkbox"
                  className="peer h-4 w-4 cursor-pointer transition-all duration-200 appearance-none rounded shadow-sm hover:shadow-md border-2 border-gray-300 checked:bg-gradient-to-br checked:from-indigo-600 checked:to-indigo-700 checked:border-indigo-600 hover:border-indigo-400"
                  id="check"
                  checked={isAllChecked}
                  onChange={onCheckAll}
                />
                <span className="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none transition-opacity duration-200">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-2.5 w-2.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="1"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                </span>
              </label>
            </div>
          </th>
        )}
        {columns.map((col, index) => {
          // Support both { field, label } and { header, accessor } formats
          const key = col.field || col.header || `col-${index}`;
          const label = col.label || col.header || "";
          const sortable = col.sortable !== undefined ? col.sortable : !!col.field;
          const className = col.className || "";

          return (
            <th
              key={key}
              className={`px-4 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider transition-colors duration-200 ${
                sortable ? "cursor-pointer hover:bg-gray-200/50 select-none" : ""
              } ${className}`}
              onClick={sortable && onSort ? () => onSort(col.field) : undefined}
            >
              <div className="flex items-center gap-2">
                <span>{label}</span>
                {sortable && sortField === col.field && (
                  <span className="text-indigo-600">
                    {sortOrder === "asc" ? (
                      <ArrowUp
                        size={14}
                        className="animate-in fade-in duration-200"
                      />
                    ) : (
                      <ArrowDown
                        size={14}
                        className="animate-in fade-in duration-200"
                      />
                    )}
                  </span>
                )}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
}
